import React, { useContext, useEffect, useMemo, useState } from 'react';
import orderBy from 'lodash/orderBy';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';
import filter from 'lodash/filter';

import { LandingContext } from '~/src/containers/ContextContainer';
import {
    IRegionsWithCitiesGroup,
    IRegionWithCities,
} from '@r1-frontend/api-domru/api-content/v1/geography/get-regions/dto/ResponseDto';
import type {
    ICitySatellite,
} from '@r1-frontend/api-domru/api-content/v1/geography/get-cities-satellites/dto/ResponseDto';
import { doRequest } from '@r1-frontend/do-request';
import { getRegionsGroups } from '@r1-frontend/api-domru/api-content/v1/geography/get-regions';
import { getTopCities } from '@r1-frontend/api-domru/api-content/v1/geography/get-top-cities';
import CitySelectComponent from '@r1-frontend/city-select';
import { IRegionCity } from '@r1-frontend/api-domru/api-content/v1/geography/get-cities-by-region';
import { getCities } from '~/src/services/api/api-content/geography/get-all-cities';
import { ApiCity } from '~/src/services/api/api-content/geography/types';

interface IProps {
    handleCitySave: (domain?: ICitySatellite) => void,
}

type TCitySatelliteByApiCity = ICitySatellite & Pick<ApiCity, 'region_id' | 'parent_city_id'>

export const CitySelect = ({ handleCitySave }: IProps): JSX.Element => {
    const { city } = useContext(LandingContext);

    const [regions, setRegions] = useState<IRegionsWithCitiesGroup>({});
    const [topCities, setTopCities] = useState<ICitySatellite[]>([]);
    const [satellites, setSatellites] = useState<TCitySatelliteByApiCity[]>([]);
    const [selectedRegion, setSelectedRegion] = useState<IRegionWithCities | null>(null);
    const providerId = useMemo(() => city?.provider_id || 47, [city]);

    useEffect(() => {
        if (isEmpty(regions) && isEmpty(topCities) && isEmpty(satellites)) {
            (async () => {
                const [respRegion, respTopCities, respSatellites] = await Promise.all([
                    doRequest.apiContent<IRegionsWithCitiesGroup>(
                        getRegionsGroups(providerId, { sortBy: 'name', groupBy: 'firstChar' }),
                    ),
                    doRequest.apiContent<ICitySatellite[]>(
                        getTopCities(providerId),
                    ),
                    getCities(['name', 'domain', 'provider_id', 'is_active', 'region_id', 'parent_city_id']),
                ]);

                setRegions(respRegion.isSuccess ? respRegion.payload : {});
                setTopCities(respTopCities.isSuccess ? respTopCities.payload : []);
                setSatellites(respSatellites.filter(city => city.is_active));
            })();
        }
    }, [providerId, regions, satellites, topCities]);

    const updateRegions = (region: IRegionWithCities, cities: IRegionCity[]) => {
        const regionLetter = region.name.charAt(0);
        const letterRegions = [...regions[regionLetter]];
        const updatedRegionIndex = letterRegions.findIndex(_region => _region.region_id === region.region_id);

        if (updatedRegionIndex !== -1) {
            letterRegions[updatedRegionIndex] = { ...letterRegions[updatedRegionIndex], cities };
            setRegions({
                ...regions,
                [regionLetter]: letterRegions,
            });
        }
    };

    const fetchRegionCities = async (region: IRegionWithCities): Promise<void> => {
        if (region.region_id === selectedRegion?.region_id) {
            setSelectedRegion(null);
            return;
        }

        if (!region.cities?.length) {
            const citiesByRegion = map(
                filter(satellites, { region_id: region.region_id }),
                ({ name, domain, provider_id, parent_city_id }) => ({
                    name,
                    domain,
                    provider_id,
                    main: parent_city_id ? 0 : 1,
                })
            );

            const cities: IRegionCity[] = orderBy(citiesByRegion, ['main', 'name'], ['desc', 'asc']);
            setSelectedRegion({ ...region, cities });
            updateRegions(region, cities);
            return;
        }
        setSelectedRegion(region);
    };

    return (
        <CitySelectComponent
            regions={regions}
            topCities={topCities}
            citiesSatellites={satellites}
            onRegionSelect={fetchRegionCities}
            onCitySelect={handleCitySave}
            selectedRegion={selectedRegion}
            data-test='city-select'
        />
    );
};

